import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../../assets/assetKeys';
import { VISUAL_THEME } from '../../assets/visualTheme';
import { GAME_HEIGHT, GAME_WIDTH } from '../../data/gameConstants';
import { ObstacleRect } from '../../systems/ZoneCoreSystems';
import { UIStyle } from '../../ui/UIStyle';

/** Semua titik interaksi di koridor jalur datar (y ≈ 520–560), bukan di lereng bukit. */
export const ZONE2_POSITIONS = {
  SPAWN: { x: 120, y: 590 },
  ANIMAL_TRIGGER: { x: 400, y: 545 },
  MONKEY_TREE: { x: 360, y: 455 },
  BUSH_TIGER: { x: 560, y: 545 },
  SAFE_ROUTE: { x: 780, y: 545 },
  KERIS: { x: 960, y: 545 },
  EXIT: { x: 1180, y: 545 },
  EXIT_BLOCK: { x: 1080, y: 545, width: 40, height: 120 },
} as const;

export class Zone2Map {
  private scene: Phaser.Scene;
  private exitBlocker?: Phaser.GameObjects.Rectangle;
  private exitLabel?: Phaser.GameObjects.Text;
  private tenseOverlay?: Phaser.GameObjects.Rectangle;
  private decorTrees: Phaser.GameObjects.Image[] = [];
  private kerisBeacon: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  build(): ObstacleRect[] {
    this.drawBackground();
    this.drawHills();
    this.drawPaths();
    this.drawDecorations();
    return this.buildObstacles();
  }

  setTenseAtmosphere(enabled: boolean): void {
    if (enabled) {
      if (!this.tenseOverlay) {
        this.tenseOverlay = this.scene.add.rectangle(
          GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x1a1a2e, 0.15,
        ).setDepth(4);
      }
      this.tenseOverlay.setVisible(true);
      this.decorTrees.forEach((t) => t.setTint(0xbbddbb));
    } else {
      this.tenseOverlay?.setVisible(false);
      this.decorTrees.forEach((t) => t.clearTint());
    }
  }

  openExitPath(): void {
    this.exitBlocker?.destroy();
    this.exitBlocker = undefined;
    this.exitLabel?.setColor('#52b788');
    this.exitLabel?.setText('Rumah Bubungan (Terbuka)');
  }

  /** Tampilkan landmark Keris 1 setelah tanda alam selesai. */
  revealKerisBeacon(): void {
    if (this.kerisBeacon.length > 0) return;

    const x = ZONE2_POSITIONS.KERIS.x;
    const y = ZONE2_POSITIONS.KERIS.y;

    const sign = this.scene.add.image(x, y - 28, TEXTURE_KEYS.SIGN_BOARD).setScale(1.05).setDepth(8);
    const kerisIcon = this.scene.add.image(x, y - 4, TEXTURE_KEYS.KERIS_1).setScale(0.85).setDepth(9);
    const ring = this.scene.add.circle(x, y, 42, 0xf2c14e, 0.18).setDepth(7);
    this.scene.tweens.add({
      targets: ring,
      scaleX: 1.3,
      scaleY: 1.3,
      alpha: 0,
      duration: 900,
      repeat: -1,
    });

    this.kerisBeacon = [sign, kerisIcon, ring];
  }

  getKerisPedestalPosition(): { x: number; y: number } {
    return { x: ZONE2_POSITIONS.KERIS.x, y: ZONE2_POSITIONS.KERIS.y };
  }

  getExitBlockerRect(): ObstacleRect | null {
    if (!this.exitBlocker) return null;
    const fb = ZONE2_POSITIONS.EXIT_BLOCK;
    return { x: fb.x, y: fb.y, width: fb.width, height: fb.height };
  }

  private drawBackground(): void {
    this.scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, TEXTURE_KEYS.BG_ZONE2)
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
      .setDepth(0);

    this.scene.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT,
      VISUAL_THEME.zoneWarmth.zone2, 0.08,
    ).setDepth(0.5);

    const ground = this.scene.add.graphics().setDepth(1);
    ground.fillStyle(0x4a7c59, 0.9);
    ground.fillRect(0, 470, GAME_WIDTH, GAME_HEIGHT - 470);
    ground.fillStyle(0x9ad6c1, 0.14);
    ground.fillRect(0, 336, GAME_WIDTH, 28);
    ground.fillStyle(0x5ca67d, 0.18);
    ground.fillRect(0, 392, GAME_WIDTH, 34);
    ground.fillStyle(0x4a7c59, 0.2);
    ground.fillEllipse(240, 430, 340, 84);
    ground.fillEllipse(700, 412, 520, 96);
    ground.fillEllipse(1080, 430, 360, 88);
    ground.fillStyle(0x78c97f, 0.88);
    ground.fillEllipse(GAME_WIDTH / 2, 535, 1080, 220);
    ground.fillStyle(0xffffff, 0.08);
    ground.fillEllipse(540, 566, 170, 34);
    ground.fillEllipse(820, 560, 140, 30);
  }

  /** Bukit hanya latar belakang — tidak bisa didaki. */
  private drawHills(): void {
    const g = this.scene.add.graphics().setDepth(1.2);
    g.fillStyle(0x6ee7a8, 0.45);
    g.fillTriangle(60, 500, 200, 350, 280, 500);
    g.fillStyle(0x3f5f45, 0.2);
    g.fillTriangle(660, 470, 940, 250, 1180, 470);
    g.fillStyle(0x6b4f36, 0.18);
    g.fillTriangle(740, 470, 860, 340, 920, 470);
    g.fillTriangle(960, 470, 1015, 330, 1120, 470);

    this.scene.add.text(880, 300, 'Bukit Tinggi (Lereng Curam)', {
      ...UIStyle.mapLabel(14, '#fff3d6'),
    }).setOrigin(0.5).setDepth(1.5);
  }

  private drawPaths(): void {
    const path = this.scene.add.graphics().setDepth(2);
    path.fillStyle(0xb3926f, 0.52);
    const pts = [
      { x: 120, y: 585 },
      { x: 320, y: 565 },
      { x: 520, y: 550 },
      { x: 720, y: 542 },
      { x: 920, y: 545 },
      { x: 1150, y: 552 },
    ];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      path.fillEllipse((a.x + b.x) / 2, (a.y + b.y) / 2, 100, 36);
    }

    path.fillStyle(0xe7c487, 0.16);
    path.fillEllipse(230, 575, 90, 18);
    path.fillEllipse(520, 555, 120, 20);
    path.fillEllipse(860, 548, 110, 18);
  }

  private drawDecorations(): void {
    const tree = (x: number, y: number, s = 0.7, flip = false) => {
      const t = this.scene.add.image(x, y, TEXTURE_KEYS.TREE_LARGE).setScale(s).setDepth(3);
      if (flip) t.setFlipX(true);
      this.decorTrees.push(t);
      return t;
    };

    tree(100, 430, 0.65);
    tree(200, 448, 0.62);
    tree(280, 410, 0.55);
    tree(ZONE2_POSITIONS.MONKEY_TREE.x, ZONE2_POSITIONS.MONKEY_TREE.y, 0.78);
    tree(720, 400, 0.6, true);
    tree(1040, 405, 0.72, true);
    tree(1160, 430, 0.55, true);

    const bush = (x: number, y: number) => {
      this.scene.add.ellipse(x, y, 50, 30, 0x40916c, 0.75).setDepth(3);
      this.scene.add.ellipse(x + 15, y - 8, 35, 22, 0x52b788, 0.65).setDepth(3);
    };
    bush(300, 545);
    bush(ZONE2_POSITIONS.BUSH_TIGER.x, ZONE2_POSITIONS.BUSH_TIGER.y + 8);
    bush(650, 548);
    bush(1020, 552);

    this.scene.add.image(480, 538, TEXTURE_KEYS.RAFFLESIA).setScale(0.38).setDepth(2.8).setAlpha(0.85);
    this.scene.add.image(850, 542, TEXTURE_KEYS.RAFFLESIA).setScale(0.32).setDepth(2.8).setAlpha(0.75);
    this.scene.add.image(1100, 548, TEXTURE_KEYS.RAFFLESIA).setScale(0.36).setDepth(2.8).setAlpha(0.8);

    this.scene.add.image(
      ZONE2_POSITIONS.ANIMAL_TRIGGER.x,
      ZONE2_POSITIONS.ANIMAL_TRIGGER.y - 18,
      TEXTURE_KEYS.SIGN_BOARD,
    ).setScale(0.9).setDepth(4);

    this.scene.add.image(340, 395, TEXTURE_KEYS.KERA).setScale(0.58).setDepth(3).setAlpha(0.45).setFlipX(true);

    this.exitLabel = UIStyle.addMapLabel(
      this.scene,
      ZONE2_POSITIONS.EXIT.x,
      ZONE2_POSITIONS.EXIT.y - 50,
      'Rumah Bubungan (Terkunci)',
      15,
      '#ff6b6b',
    ).setDepth(5);

    this.scene.add.text(ZONE2_POSITIONS.MONKEY_TREE.x, ZONE2_POSITIONS.MONKEY_TREE.y - 70,
      'Hutan mendadak sunyi', {
        ...UIStyle.mapLabel(14, '#fff8e7'),
      }).setOrigin(0.5).setDepth(5);
  }

  private buildObstacles(): ObstacleRect[] {
    const obs: ObstacleRect[] = [
      // Langit + bukit — blok seluruh area atas (sama seperti Zona 1)
      { x: GAME_WIDTH / 2, y: 165, width: GAME_WIDTH, height: 330 },
      // Lantai koridor — cegah turun ke bawah peta
      { x: GAME_WIDTH / 2, y: GAME_HEIGHT - 20, width: GAME_WIDTH, height: 40 },
      // Dinding kiri/kanan
      { x: 30, y: GAME_HEIGHT / 2, width: 60, height: GAME_HEIGHT },
      { x: GAME_WIDTH - 20, y: GAME_HEIGHT / 2, width: 40, height: GAME_HEIGHT },
      // Semak/bukit latar — blok naik ke lereng (perpanjang ke bawah)
      { x: 170, y: 405, width: 320, height: 110 },
      { x: 680, y: 398, width: 460, height: 110 },
      { x: 1085, y: 405, width: 290, height: 110 },
      // Pohon dekor (hanya di tepi jalur, bukan di tengah)
      { x: 160, y: 430, width: 60, height: 80 },
      { x: 200, y: 448, width: 55, height: 80 },
      { x: ZONE2_POSITIONS.MONKEY_TREE.x, y: ZONE2_POSITIONS.MONKEY_TREE.y, width: 55, height: 80 },
      { x: 720, y: 400, width: 50, height: 70 },
      { x: 1040, y: 405, width: 55, height: 75 },
      { x: 1160, y: 425, width: 50, height: 70 },
      // Penghalang lereng kanan — tutup celah naik ke bukit
      { x: 820, y: 430, width: 280, height: 90 },
      { x: 560, y: 430, width: 120, height: 70 },
      // Tutup celah antara semak kiri & tengah (x ≈ 330–450)
      { x: 395, y: 395, width: 150, height: 100 },
    ];

    const fb = ZONE2_POSITIONS.EXIT_BLOCK;
    this.exitBlocker = this.scene.add.rectangle(fb.x, fb.y, fb.width, fb.height, 0xc1121f, 0.35);
    this.exitBlocker.setDepth(6);
    obs.push({ x: fb.x, y: fb.y, width: fb.width, height: fb.height });

    return obs;
  }
}
